import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Tooltip,
  Typography,
} from '@mui/material';

import { makeAuthenticatedApiCall } from '../../../apiHelper';
import { pieceDetachmentEndpoints, requestPieceDetachment } from '../../../utils/pieceDetachment';
import theme from '../../../theme';
import FirebaseUploadComponent from '../../CreateCotation/FirebaseUploadComponent';
import CfnPiecesWarning from './CfnPiecesWarning';

interface UploadModalProps {
  dealId: number;
  photos: Record<string, string[]>;
  extUrl?: string;
  regPlate?: string;
}

const UploadModalMandate: React.FC<UploadModalProps> = ({ dealId, photos, extUrl, regPlate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const [signedPurchaseDeclarationUrls, setSignedPurchaseDeclarationUrls] = useState<string[]>(
    photos?.signedPurchaseDeclaration || [],
  );
  const [signedCessionCertificateUrls, setSignedCessionCertificateUrls] = useState<string[]>(
    photos?.signedCessionCertificate || [],
  );

  // Auto-open modal if action=openMandateProcurementModal in URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const action = searchParams.get('action');
    const autoSearch = searchParams.get('autoSearch');

    const shouldOpen =
      action === 'openMandateProcurementModal' &&
      !open &&
      (!autoSearch ||
        regPlate?.toLowerCase().replace(/[^a-z0-9]/g, '') === autoSearch.toLowerCase().replace(/[^a-z0-9]/g, ''));

    if (shouldOpen) {
      setOpen(true);
      searchParams.delete('action');
      const newSearch = searchParams.toString();
      navigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : '',
        },
        { replace: true },
      );
    }
  }, [location, navigate, open, regPlate, dealId]);

  const totalFiles = signedPurchaseDeclarationUrls.length + signedCessionCertificateUrls.length;
  const allFilesUploaded = signedPurchaseDeclarationUrls.length > 0 && signedCessionCertificateUrls.length > 0;
  const isValidationDisabled = !allFilesUploaded;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleUploadComplete = (url: string, category: string) => {
    if (category === 'signedPurchaseDeclaration') {
      setSignedPurchaseDeclarationUrls((prev) => [...prev, url]);
    } else if (category === 'signedCessionCertificate') {
      setSignedCessionCertificateUrls((prev) => [...prev, url]);
    }
  };

  // Retrait en base AVANT l'objet Storage (cf. UploadModal).
  const handleBeforeDelete = (url: string) => requestPieceDetachment(pieceDetachmentEndpoints.deal(dealId), url);

  const handleDeleteComplete = (url: string, category: string) => {
    if (category === 'signedPurchaseDeclaration') {
      setSignedPurchaseDeclarationUrls((prev) => prev.filter((u) => u !== url));
    } else if (category === 'signedCessionCertificate') {
      setSignedCessionCertificateUrls((prev) => prev.filter((u) => u !== url));
    }
  };

  const handleValidation = async () => {
    try {
      await makeAuthenticatedApiCall('post', `/api/deals/${dealId}/update-car-deal-procurement-file-mandate`, {
        photos: {
          signedPurchaseDeclaration: signedPurchaseDeclarationUrls,
          signedCessionCertificate: signedCessionCertificateUrls,
        },
      });

      if (allFilesUploaded) {
        await makeAuthenticatedApiCall('post', `/api/mails/procurement-file-admin-notification`, {
          dealId,
          extUrl,
          regPlate,
          isMandate: true,
          isDepositSale: false,
        });
      }

      handleClose();
    } catch (error) {
      console.error('Erreur lors de la mise à jour des photos', error);
    }
  };

  const buttonColor = allFilesUploaded ? '#4caf50' : theme.palette.saffron.main;

  return (
    <>
      <Button
        variant="contained"
        onClick={handleOpen}
        className="uploadModalButton"
        sx={{
          backgroundColor: buttonColor,
          '&:hover': { backgroundColor: buttonColor },
          transition: 'background-color 0.3s ease',
        }}
      >
        {allFilesUploaded ? '✅' : '📤'} Dossier de vente Skipcar {totalFiles > 0 ? `(${totalFiles})` : ''}
      </Button>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
        <DialogTitle sx={{ textAlign: 'center', pb: 1, pt: 3 }}>
          <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
            Documents attendus pour le véhicule {regPlate}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 4, pt: 2 }}>
          {/* Documents Checklist */}
          <Box
            sx={{
              backgroundColor: '#fafafa',
              borderLeft: '3px solid #1a1a1a',
              padding: '20px 24px',
              marginBottom: '24px',
              borderRadius: '4px',
            }}
          >
            <Typography
              sx={{
                margin: '0 0 20px 0',
                fontSize: '14px',
                fontWeight: 600,
                color: '#1a1a1a',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Récapitulatif des documents à déposer
            </Typography>

            {/* Document 1 - Déclaration d'achat */}
            <Box
              sx={{
                margin: '0 0 16px 0',
                padding: '16px',
                backgroundColor: '#ffffff',
                borderRadius: '6px',
                border: '1px solid #e5e5e5',
              }}
            >
              <Typography sx={{ margin: '0 0 8px 0', fontSize: '15px', color: '#1a1a1a', fontWeight: 600 }}>
                Déclaration d'achat
              </Typography>
              <Typography sx={{ margin: 0, fontSize: '14px', color: '#6a6a6a', lineHeight: '20px' }}>
                Complétée, datée et signée le jour de la vente
              </Typography>
            </Box>

            {/* Document 2 - Certificat de cession */}
            <Box
              sx={{
                padding: '16px',
                backgroundColor: '#ffffff',
                borderRadius: '6px',
                border: '1px solid #e5e5e5',
              }}
            >
              <Typography sx={{ margin: '0 0 8px 0', fontSize: '15px', color: '#1a1a1a', fontWeight: 600 }}>
                Certificat de cession
              </Typography>
              <Typography sx={{ margin: 0, fontSize: '14px', color: '#6a6a6a', lineHeight: '20px' }}>
                Complété, daté et signé le jour de la vente
              </Typography>
            </Box>
          </Box>

          {/* Important Note */}
          <Box
            sx={{
              backgroundColor: '#f9f9f9',
              borderRadius: '6px',
              padding: '16px 20px',
              marginBottom: '32px',
            }}
          >
            <Typography sx={{ margin: 0, fontSize: '14px', lineHeight: '20px', color: '#4a4a4a' }}>
              <strong style={{ color: '#1a1a1a' }}>📋 À savoir :</strong> Le contrat sera validé à réception des
              documents dûment complétés.
            </Typography>
          </Box>
        </DialogContent>
        <DialogContent sx={{ pt: 0 }}>
          <CfnPiecesWarning compact />
        </DialogContent>

        {/* 2 Dropzones side by side */}
        <Grid container item xs={12}>
          <Grid item xs={12} md={6}>
            <DialogTitle>Déclaration d'achat signée</DialogTitle>
            <DialogContent>
              <FirebaseUploadComponent
                onBeforeDelete={handleBeforeDelete}
                onDeleteComplete={(url) => handleDeleteComplete(url, 'signedPurchaseDeclaration')}
                onUploadComplete={(url) => handleUploadComplete(url, 'signedPurchaseDeclaration')}
                uploadedUrls={signedPurchaseDeclarationUrls}
              />
            </DialogContent>
          </Grid>
          <Grid item xs={12} md={6}>
            <DialogTitle>Certificat de cession signé</DialogTitle>
            <DialogContent>
              <FirebaseUploadComponent
                onBeforeDelete={handleBeforeDelete}
                onDeleteComplete={(url) => handleDeleteComplete(url, 'signedCessionCertificate')}
                onUploadComplete={(url) => handleUploadComplete(url, 'signedCessionCertificate')}
                uploadedUrls={signedCessionCertificateUrls}
              />
            </DialogContent>
          </Grid>

          <DialogActions style={{ justifyContent: 'flex-end', width: '100%', flexDirection: 'row' }}>
            <Button className="secondaryDealActionButton" onClick={handleClose}>
              Annuler
            </Button>
            <Tooltip
              title="Les 2 documents doivent être déposés avant de pouvoir valider"
              placement="top"
              disableHoverListener={!isValidationDisabled}
            >
              <span style={{ marginRight: '30px' }}>
                <Button
                  className="dealActionButton"
                  onClick={handleValidation}
                  variant="contained"
                  color="primary"
                  disabled={isValidationDisabled}
                >
                  Valider les fichiers
                </Button>
              </span>
            </Tooltip>
          </DialogActions>
        </Grid>
      </Dialog>
    </>
  );
};

export default UploadModalMandate;
